import { useEffect, useMemo, useState } from 'react';
import { Icon, IconButton, LinearProgress, TablePagination, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Button, Typography, AlertColor, Box } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GrupoTesteService } from 'shared/services/grupoTeste/grupoTesteService';
import { CanaisService } from 'shared/services/canais/canaisService';
import { IListagemGrupoTeste, IListagemGrupoTesteDestinatario, listKeyValueTipoGrupoTeste } from 'shared/types';
import { FerramentasDaListagem, IListaFiltros, AlertaSnackBar, ConfirmacaoModal } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { useSelectedOrganization } from 'shared/store/organization';
import { getValueFromKeyValue, IKeyValue, listagemCanalToIKeyValue } from 'shared/utils';
import { useHasPermissionAcao } from 'shared/hooks';

export const ListagemGrupoTesteDestinatario: React.FC = () => {
  const { idCanal } = useParams<'idCanal'>();
  const { idGrupoTeste } = useParams<'idGrupoTeste'>();

  const [searchParams, setSearchParams] = useSearchParams();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [stateSnack, setStateSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  const hasPermitionCreate = useHasPermissionAcao('grupoteste', 'CRE');
  const hasPermitionRead = useHasPermissionAcao('grupoteste', 'REA');
  const hasPermitionUpdate = useHasPermissionAcao('grupoteste', 'UPD');

  const [rows, setRows] = useState<IListagemGrupoTesteDestinatario[]>([]);

  const org = useSelectedOrganization();

  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);

  const [dest, setDestinatario] = useState(0);

  const [titleConfirmacao, setTitleConfirmacao] = useState('');
  const [messageConfirmacao, setMessageConfirmacao] = useState('');

  const [eventoPesquisar, setEventoPesquisar] = useState(false);
  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const buscaOrderBy = useMemo(() => {
    return searchParams.get('orderBy') || '';
  }, [searchParams]);

  const offSet = useMemo(() => {
    return searchParams.get('offset') || '0';
  }, [searchParams]);

  const limit = useMemo(() => {
    return searchParams.get('limit') || '10';
  }, [searchParams]);

  const handleLimpar = () => {
    // eslint-disable-next-line prefer-const
    let parametros: string[] = [];
    searchParams.forEach(function (value, key) {
      parametros.push(key);
    });
    parametros.map((item) => (
      searchParams.delete(item)
    ));
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handlePesquisar = () => {
    setPage(0);
    searchParams.set('offset', '0'); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    searchParams.set('offset', (newPage * rowsPerPage).toString()); setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    searchParams.set('limit', event.target.value.toString());
    searchParams.set('offset', '0');
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  const handleOrdenacao = (campo: string) => {
    searchParams.set('orderBy', campo);
    setSearchParams(searchParams, { replace: true });
    setEventoPesquisar(!eventoPesquisar);
  };

  
  const handleDeleteDestinatario = (id: number) => {
    setDestinatario(id);
    setTitleConfirmacao(t('components.actions.apagar'));
    setMessageConfirmacao(t('messages.dadosVariaveis.exclusion') + id + '?');
    setOpenConfirmacao(true);
    console.log(id);
    // setEventoPesquisar(!eventoPesquisar);
  };

  const confirmDelete = () => {
    GrupoTesteService.deleteDestinatarioById(Number(org?.CodigoOrganizacao), Number(idCanal), Number(idGrupoTeste), dest)
      .then(result => {
        if (result instanceof Error) {
          setAlert({
            text: result.message,
            type: 'error',
          });
          setOpenConfirmacao(false);
        } else {
          setAlert({
            text: 'Registro apagado com sucesso!',
            type: 'success',
          });
          setOpenConfirmacao(false);
          setEventoPesquisar(!eventoPesquisar);
        }
      })
      .catch( reason => {
        setAlert({
          text: reason,
          type: 'error',
        });
        setOpenConfirmacao(false);
        setEventoPesquisar(!eventoPesquisar);
      }); 
  };


  const handleCloseSnack = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setStateSnack({ ...stateSnack, open: false });
  };

  useEffect(() => {
    setIsLoading(true);

    GrupoTesteService.getAllDestinatario(Number(org?.CodigoOrganizacao), Number(idCanal), Number(idGrupoTeste), searchParams.toString())
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setStateSnack({ open: true, severity: 'error', message: `Ocorreu o seguinte erro: ${result.message}` });
        } else {
          setTotalCount(result.count);
          setRows(result.rows);
          setRowsPerPage(Number.parseInt(limit.toString()));
          setPage(Number.parseInt(offSet.toString()) / Number.parseInt(limit.toString()));
        }
      });

  }, [eventoPesquisar]);

  return (
    <>
      <LayoutBaseDePagina
        titulo={t('pages.grupoTesteDestinatario.titulo')}
        botaoLog={
          <Button
            color='info'
            disableElevation
            variant='contained'
            size='small'
            onClick={() => navigate('/grupoteste/listagem/logsDestinatario')}
            startIcon={<Icon>crop</Icon>}
          >
            <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
              {t('components.ferramentasDaListagem.logs')}
            </Typography>
          </Button>
        }
      >
        
        <TableContainer component={Paper} variant="outlined" sx={{ m: 1, width: 'auto' }}>
          <Table stickyHeader >
            <TableHead>
              <TableRow>
                <TableCell onDoubleClick={() => handleOrdenacao(buscaOrderBy === 'NomeDestinatario_ASC' ? 'NomeDestinatario_DESC' : 'NomeDestinatario_ASC')}>
                  {t('pages.grupoTesteDestinatario.nome')}
                  <Icon fontSize="inherit" sx={{ verticalAlign: 'middle' }}>{buscaOrderBy === 'NomeDestinatario_ASC' ? 'north' : buscaOrderBy === 'NomeDestinatario_DESC' ? 'south' : ''}</Icon>
                </TableCell>
                <TableCell onDoubleClick={() => handleOrdenacao(buscaOrderBy === 'DescricaoDestinatario_ASC' ? 'DescricaoDestinatario_DESC' : 'DescricaoDestinatario_ASC')}>
                  {t('pages.grupoTesteDestinatario.descricao')}
                  <Icon fontSize="inherit" sx={{ verticalAlign: 'middle' }}>{buscaOrderBy === 'DescricaoDestinatario_ASC' ? 'north' : buscaOrderBy === 'DescricaoDestinatario_DESC' ? 'south' : ''}</Icon>
                </TableCell>
                <TableCell width={60}>{t('global.acoes')}</TableCell>
              </TableRow>
            </TableHead>
            {!isLoading && (<TableBody>
              {rows.map((row) => (
                <TableRow key={row.IdGrupoTesteDestinatario}>
                  <TableCell>{row.NomeDestinatario}</TableCell>
                  <TableCell>{row.DescricaoDestinatario}</TableCell>
                  <TableCell>
                    <IconButton size="small" disabled={!hasPermitionUpdate} onClick={() => handleDeleteDestinatario(row.IdGrupoTesteDestinatario)}>
                      <Icon color={hasPermitionUpdate ? 'error' : 'disabled'}>delete_forever</Icon>
                    </IconButton>
                  </TableCell>

                </TableRow>
              ))}
            </TableBody>)}

            {totalCount === 0 && !isLoading && (
              <caption>{t('global.listaVazia')}</caption>
            )}

            <TableFooter>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={3}>
                    <LinearProgress variant='indeterminate' />
                  </TableCell>
                </TableRow>
              )}
              {(!isLoading) && (
                <TableRow>
                  <TableCell colSpan={1}>
                    <Box gap={1} display="flex">
                      {hasPermitionCreate && (<Button
                        color='primary'
                        disableElevation
                        variant='outlined'
                        onClick={() => navigate(`/grupoteste/detalhe/${idCanal}/${idGrupoTeste}/destinatario/novo`)}
                        startIcon={<Icon>add</Icon>}
                      >
                        <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                          {t('components.actions.novo')}
                        </Typography>
                      </Button>)}
                      <Button
                        color='primary'
                        disableElevation
                        variant='outlined'
                        onClick={() => navigate(`/grupoteste/detalhe/${idCanal}/${idGrupoTeste}`)}
                        startIcon={<Icon>arrow_back</Icon>}
                      >
                        <Typography variant='button' whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                          {t('components.actions.voltar')}
                        </Typography>
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell colSpan={2}>
                    <TablePagination
                      component="div"
                      labelDisplayedRows={({ from, to, count }) => `${from} - ${to} ${t('global.of')} ${count !== -1 ? count : `${t('global.moreThan')} ${to}`}`}
                      labelRowsPerPage={t('global.rowsPerPage')}
                      showFirstButton={true}
                      showLastButton={true}
                      page={page}
                      count={totalCount}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[5, 8, 10, 20, 50, 100]}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                    //onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableFooter>
          </Table>
        </TableContainer>
        <ConfirmacaoModal
          // key={row.OrgCod}
          open={openConfirmacao}
          title={titleConfirmacao}
          message={messageConfirmacao}
          onClose={() => setOpenConfirmacao(false)}
          onConfirm={() => confirmDelete()}
        />
      </LayoutBaseDePagina>
      <AlertaSnackBar
        open={stateSnack.open}
        severity={stateSnack.severity as AlertColor}
        message={stateSnack.message}
        handleClose={handleCloseSnack}
      />
    </>
  );
};

function setAlert(arg0: null) {
  throw new Error('Function not implemented.');
}
